// Encyclopedia portraits: draws an enemy's own procedural model (src/render/enemyArt) into a small 2D canvas once and
// caches it as an image URL. A tiny flat-shaded software rasteriser (painter's sort, three-step cel light, ink rim) on a
// Canvas 2D context: no GPU work, no render targets or read-backs, identical on WebGPU and WebGL2. Render-only.
import { MAPS } from '../maps/registry';
import { mapRender } from '../maps/renderRegistry';
import * as THREE from 'three/webgpu';
import type { EnemyKind } from '../core/content';
import { BOSS_ART, FAMILY_ART } from './enemyArt';

export interface PortraitArt { kind: EnemyKind; elite: boolean; boss: string | null }

interface Tri { z: number; pts: Float32Array; col: [number, number, number]; glow: boolean }
const geoCache = new Map<string, { tris: Tri[]; minX: number; maxX: number; minY: number; maxY: number }>();
const imgCache = new Map<string, string>();

const YAW = -0.55, PITCH = 0.2; // three-quarter view from the front-left, a little above
const L = new THREE.Vector3(-0.45, 0.75, 0.5).normalize();
const toSrgb = (c: number) => Math.round(255 * Math.min(1, Math.max(0, c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055)));

function artFor(a: PortraitArt) {
  if (a.boss) return BOSS_ART[a.boss] ?? mapBossArt(a.boss);
  return a.kind === 'boss' ? BOSS_ART['HOLLOW TYRANT'] : FAMILY_ART[a.kind as Exclude<EnemyKind, 'boss'>] ?? null;
}
const artKey = (a: PortraitArt) => (a.boss ? `boss:${a.boss}` : `${a.kind}:${a.elite ? 'e' : 'n'}`);

/** model → view-space triangles (rotated, not yet fitted), depth-sorted far to near */
function prepare(a: PortraitArt) {
  const key = artKey(a);
  const hit = geoCache.get(key); if (hit) return hit;
  const art = artFor(a); if (!art) return null;
  const geo = art.build(a.elite);
  const rot = new THREE.Matrix4().makeRotationX(PITCH).multiply(new THREE.Matrix4().makeRotationY(YAW));
  const tris: Tri[] = [];
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  const v = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()], e1 = new THREE.Vector3(), e2 = new THREE.Vector3(), n = new THREE.Vector3();
  for (const [g, glow] of [[geo.body, false], [geo.glow, true]] as const) {
    const pos = g.getAttribute('position'), col = g.getAttribute('color');
    const idx = g.index;
    const count = idx ? idx.count : pos.count;
    for (let i = 0; i + 2 < count; i += 3) {
      const ii = [0, 1, 2].map((k) => (idx ? idx.getX(i + k) : i + k));
      for (let k = 0; k < 3; k++) v[k].fromBufferAttribute(pos, ii[k]).applyMatrix4(rot);
      e1.subVectors(v[1], v[0]); e2.subVectors(v[2], v[0]); n.crossVectors(e1, e2);
      const len = n.length(); if (len < 1e-9) continue;
      n.multiplyScalar(1 / len); if (n.z < 0) n.negate(); // lighting is two-sided (no culling: painter's order hides backs)
      const k = Math.max(0, n.dot(L));
      const shade = glow ? 1.25 : k > 0.55 ? 1.0 : k > 0.15 ? 0.74 : 0.5;
      const c = col ? [col.getX(ii[0]), col.getY(ii[0]), col.getZ(ii[0])] : [0.6, 0.6, 0.6];
      const pts = new Float32Array([v[0].x, v[0].y, v[1].x, v[1].y, v[2].x, v[2].y]);
      for (const q of v) { minX = Math.min(minX, q.x); maxX = Math.max(maxX, q.x); minY = Math.min(minY, q.y); maxY = Math.max(maxY, q.y); }
      tris.push({ z: (v[0].z + v[1].z + v[2].z) / 3 + (glow ? 0.02 : 0), pts, col: [toSrgb(c[0] * shade), toSrgb(c[1] * shade), toSrgb(c[2] * shade)], glow });
    }
  }
  geo.body.dispose(); geo.glow.dispose();
  tris.sort((p, q) => p.z - q.z);
  const out = { tris, minX, maxX, minY, maxY };
  geoCache.set(key, out);
  return out;
}

/**
 * Portrait image URL (PNG data URL, transparent background) of an enemy at `size` px. `silhouette` draws the locked
 * "???" form: the outline shape only, no colours. Cached per art + size + mode.
 */
export function enemyPortrait(a: PortraitArt, size: number, silhouette = false): string {
  const ck = `${artKey(a)}|${size}|${silhouette ? 's' : 'c'}`;
  const hit = imgCache.get(ck); if (hit) return hit;
  const m = prepare(a); if (!m) return '';
  const cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d'); if (!ctx) return '';
  const pad = size * 0.07, w = m.maxX - m.minX, h = m.maxY - m.minY;
  const s = (size - 2 * pad) / Math.max(w, h, 1e-3);
  const ox = size / 2 - ((m.minX + m.maxX) / 2) * s, oy = size / 2 + ((m.minY + m.maxY) / 2) * s;
  const path = (t: Tri) => { const p = t.pts; ctx.beginPath(); ctx.moveTo(ox + p[0] * s, oy - p[1] * s); ctx.lineTo(ox + p[2] * s, oy - p[3] * s); ctx.lineTo(ox + p[4] * s, oy - p[5] * s); ctx.closePath(); };
  ctx.lineJoin = 'round';
  // 1) ink: every triangle stroked wide in the outline colour (only the rim survives step 2)
  const ink = silhouette ? 'rgba(150,120,255,.55)' : '#0b0612';
  ctx.fillStyle = ink; ctx.strokeStyle = ink; ctx.lineWidth = Math.max(2, size / 48);
  for (const t of m.tris) { path(t); ctx.fill(); ctx.stroke(); }
  // 2) fills, far to near, each with a hairline of its own colour so no seams show between triangles
  ctx.lineWidth = 0.9;
  for (const t of m.tris) {
    const c = silhouette ? '#120a24' : `rgb(${t.col[0]},${t.col[1]},${t.col[2]})`;
    ctx.fillStyle = c; ctx.strokeStyle = c; path(t); ctx.fill(); ctx.stroke();
  }
  // 3) glow parts bloom softly (eyes, cores, crystal seams)
  if (!silhouette) {
    ctx.save(); ctx.globalCompositeOperation = 'lighter'; ctx.filter = `blur(${Math.max(1.5, size / 70)}px)`; ctx.globalAlpha = 0.65;
    for (const t of m.tris) if (t.glow) { const c = `rgb(${t.col[0]},${t.col[1]},${t.col[2]})`; ctx.fillStyle = c; path(t); ctx.fill(); }
    ctx.restore();
  }
  const url = cv.toDataURL('image/png');
  imgCache.set(ck, url);
  return url;
}

/** a map's final boss look by its announced name (map system), or null */
function mapBossArt(name: string) { const m = MAPS.find((d) => d.boss?.name === name); return m ? mapRender(m.id).bossArt ?? null : null; }
